/**
 * 异步 KV 存储（IndexedDB + localStorage 兜底）。
 *
 * 设计目标：把 sessionStorage / localStorage 的同步 JSON 写入从主线程搬出。
 *
 * 兼容性：
 *   - 浏览器不支持 IndexedDB（极老 Safari、隐私模式）→ 回退 localStorage（同步），行为退化但可用
 *   - 用户禁用持久化 / 配额已满 → write 静默失败并降级，下一次 read 走 storage 兜底
 *
 * 不替代 sessionStorage，仅用于：
 *   - 项目草稿（saveProjectDraft 的 metaJson）—> ~30-100KB，节流 1.5s 仍会卡主线程
 *   - 步骤四 storyboards shots 缓存 —> 100KB+，hydrate 时一次性回填
 *   - 后续可推广到任何「大 JSON blob」存储场景
 *
 * 用法：
 *   import { kvStore } from "../utils/kvStore";
 *   await kvStore.set("draft:meta:" + id, { foo: 1 });
 *   const v = await kvStore.get("draft:meta:" + id);
 *
 * 旧代码如要平滑迁移：
 *   const v = await kvStore.get(key, () => sessionStorage.getItem(key));
 *   await kvStore.set(key, payload, () => sessionStorage.setItem(key, JSON.stringify(payload)));
 */

const DB_NAME = "ai_video_kv";
const DB_VERSION = 1;
const STORE_NAME = "kv";

let _dbPromise = null;
let _idbAvailable = null;

function isIdbAvailable() {
  if (_idbAvailable !== null) return _idbAvailable;
  try {
    if (typeof indexedDB === 'undefined' || indexedDB === null) {
      _idbAvailable = false;
      return false;
    }
    // 部分隐私模式下 indexedDB 存在但 open 会抛错
    const test = indexedDB.open("__ai_video_probe__");
    test.onsuccess = () => {
      try { test.result.close(); } catch (e) { /* ignore */ }
      try { indexedDB.deleteDatabase("__ai_video_probe__"); } catch (e) { /* ignore */ }
    };
    _idbAvailable = true;
    return true;
  } catch (e) {
    _idbAvailable = false;
    return false;
  }
}

function openDb() {
  if (!isIdbAvailable()) return Promise.reject(new Error("IDB_UNAVAILABLE"));
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      _dbPromise = null;
      reject(e);
      return;
    }
    req.onupgradeneeded = (ev) => {
      const db = ev.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // 任何后续错误都允许自动重建（避免单次故障永久卡死）
      db.onversionchange = () => {
        try { db.close(); } catch (e) { /* ignore */ }
        _dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      _dbPromise = null;
      reject(req.error || new Error("IDB_OPEN_FAILED"));
    };
    req.onblocked = () => {
      // 其它标签仍持有旧版本：拒绝并降级
      _dbPromise = null;
      reject(new Error("IDB_BLOCKED"));
    };
  });
  return _dbPromise;
}

function tx(db, mode) {
  return db.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
}

function promisifyReq(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("IDB_REQ_FAILED"));
  });
}

export const kvStore = {
  /**
   * 异步读取。命中 IndexedDB 即返回；IDB 不可用 / 读失败 → 走 fallbackReader 拿同步兜底值。
   *
   * @param {string} key
   * @param {() => string|null} [fallbackReader] 同步 fallback；返回字符串（已 JSON.stringify）或 null
   * @returns {Promise<any|null>}
   */
  async get(key, fallbackReader) {
    if (!key) return null;
    try {
      const db = await openDb();
      const raw = await promisifyReq(tx(db, "readonly").get(key));
      if (raw === undefined || raw === null) {
        return fallbackReader ? readFallbackJson(fallbackReader) : null;
      }
      // 我们写入统一为 JSON 字符串；旧值若已是对象也兼容
      if (typeof raw === 'string') {
        try { return JSON.parse(raw); } catch (e) { return null; }
      }
      return raw;
    } catch (e) {
      return fallbackReader ? readFallbackJson(fallbackReader) : null;
    }
  },

  /**
   * 异步写入。IDB 不可用 / 写入失败 → 走 fallbackWriter 同步写 localStorage 兜底。
   *
   * @param {string} key
   * @param {any} value
   * @param {(serialized: string) => void} [fallbackWriter]
   * @returns {Promise<boolean>} true 表示 IDB 成功；false 表示降级到 fallback
   */
  async set(key, value, fallbackWriter) {
    if (!key) return false;
    const serialized = JSON.stringify(value);
    try {
      const db = await openDb();
      await promisifyReq(tx(db, "readwrite").put(key, serialized));
      return true;
    } catch (e) {
      if (typeof fallbackWriter === 'function') {
        try { fallbackWriter(serialized); } catch (e2) { /* ignore */ }
        return false;
      }
      return false;
    }
  },

  /**
   * 异步删除。
   */
  async del(key) {
    if (!key) return false;
    try {
      const db = await openDb();
      await promisifyReq(tx(db, "readwrite").delete(key));
      return true;
    } catch (e) {
      return false;
    }
  },

  /**
   * 批量获取所有 keys 的值（每条 key 单独 try/catch，一条失败不影响其它）。
   * 写场景慎用：单次扫描巨大 object store 时会比较慢（暂无索引场景够用）。
   */
  async getAllByKeys(keys) {
    if (!Array.isArray(keys) || !keys.length) return {};
    const out = {};
    for (const key of keys) {
      try {
        out[key] = await this.get(key);
      } catch (e) {
        out[key] = null;
      }
    }
    return out;
  },

  /**
   * 批量 put，串行（避免 IDB 事务竞争）。
   */
  async putMany(entries) {
    if (!Array.isArray(entries) || !entries.length) return 0;
    let n = 0;
    for (const [k, v] of entries) {
      if (await this.set(k, v)) n++;
    }
    return n;
  },

  /**
   * 暴露给外部判断 IDB 是否可用（false 时上层应当避免 set 调用并直接走 localStorage）。
   */
  isAvailable() {
    return isIdbAvailable();
  }
};

/**
 * 一个特殊便利函数：通过 kvStore 的 TTL 缓存扫描所有 entries。
 * 本系统目前不存高频小条目，暂不需要。
 */

function readFallbackJson(reader) {
  try {
    const raw = reader();
    if (raw == null || raw === '') return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}